/**
 * Deeply sorts an object by its keys to ensure deterministic order.
 *
 * @param {object} obj - The object to sort.
 * @returns {object} The sorted object.
 */
function deepSortObject(obj) {
  if (Array.isArray(obj)) {
    return obj.map(deepSortObject)
  }

  if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)
      .sort()
      .reduce((result, key) => {
        result[key] = deepSortObject(obj[key])
        return result
      }, {})
  }

  return obj
}

/**
 * Creates a deterministic query key for data fetching and caching.
 *
 * @param {string} resource - The name of the resource (e.g., 'apps', 'keywords').
 * @param {string} [action] - The action or operation being performed.
 * @param {object} [params] - An object containing query parameters or filters.
 * @returns {Array} A query key array that can be used with TanStack Query.
 *
 * @throws {Error} If the resource name is not a non-empty string.
 * @throws {Error} If params is not an object.
 * @throws {Error} If action is not a non-empty string.
 *
 * @example
 * // Fetching all apps
 * const queryKey = createQueryKey('apps');
 * // Result: ['apps', 'fetch', {}]
 *
 * @example
 * // Fetching a list of keywords with filters
 * const queryKey = createQueryKey('keywords', 'list', { popularity: 20, order: 'asc' });
 * // Result: ['keywords', 'list', { order: 'asc', popularity: 20 }]
 *
 * @example
 * // Fetching details of a specific app
 * const queryKey = createQueryKey('apps', 'detail', { trackId: '571800810' });
 * // Result: ['apps', 'detail', { trackId: '571800810' }]
 */
export function createQueryKey(resource, action = 'fetch', params = {}) {
  if (typeof resource !== 'string' || resource.trim() === '') {
    throw new Error('Resource name must be a non-empty string.')
  }

  if (typeof params !== 'object' || params === null || Array.isArray(params)) {
    throw new Error('Params must be an object.')
  }

  if (typeof action !== 'string' || action.trim() === '') {
    throw new Error('Action must be a non-empty string.')
  }

  const sortedParams = deepSortObject(params)

  return [resource, action, sortedParams]
}
